import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import CustomScrollbars from '../../../components/structure/CustomScrollbars';
import { color_shades_dark, color_shades_darkest, color_shades_lighter } from '../../../constants/colors';
import LiveChatBubble from './LiveChatBubble';
import Button from '../../../components/general/Button';
import Scrollbars from 'react-custom-scrollbars';

interface SessionReviewProps {
    applicationId: string,
    setEventsToShow: (events: any[]) => void
    events: any[],
    manualHeight?: any,
    hideBorder?: boolean,
    simple?: boolean,
    inModal?: boolean
}

const SessionReview = (props: SessionReviewProps) => {
    const AlwaysScrollToBottom = () => {
        const elementRef = useRef<any>();
        useEffect(() => elementRef.current.scrollIntoView());
        return <div ref={elementRef} />;
    };

    const renderRequestEventBubbles = (reqId: string, events: any[], idx: number) => {
        if (events?.length && events.every(e => e.eventType === "CallComplete")) {
            return <HiddenDiv key={idx}></HiddenDiv>
        }
        // request recieved on left, assistant speach started and content hit on right
        const requestRecEvents = events.filter(e => e.eventType === "RequestReceived");
        const contentHitEvents = events.filter(e => e.eventType === "ContentHit");
        return (
            <RequestDetails key={`${idx}-${reqId}`}>
                <LiveChatBubble simple={props.simple} markdown={generateUserMarkdown(requestRecEvents, contentHitEvents, idx === 0)} sender={"user"} showCodeModal={() => props.setEventsToShow(events)} />
                <LiveChatBubble markdown={generateBotMarkdown(events)} sender={"bot"} />
            </RequestDetails>
        )
    }
    const generateUserMarkdown = (requestEvents: any[], contentHitEvents: any[], first): string => {
        if (props.simple) {
            // get last events
            if (requestEvents?.length) {
                const lastRequest = requestEvents[requestEvents.length - 1];
                if (lastRequest.utterance?.length) {
                    return lastRequest.utterance;
                }                
            } if (contentHitEvents?.length) {
                const lastContentHit = contentHitEvents[contentHitEvents.length - 1];
                if (lastContentHit?.originalRequest?.nlp?.utterance?.length) {
                    return lastContentHit?.originalRequest?.nlp?.utterance;
                }
            }
            return first ? "Start Conversation" : "Unknown";
        }
        let md = "";
        for (let i = 0; i < requestEvents.length; i++) {
            const event = requestEvents[i];        
            let utterance = event.utterance;
            if (!utterance?.length) {
                // check if content hit array has same index
                const contentHitEvent = contentHitEvents[i];
                utterance = contentHitEvent?.originalRequest?.nlp?.utterance;                
            }
            md = `${md}${utterance ?? (first ? "Start Conversation" : "Unknown")}\n\n`;
        }
        return md
    }
    const generateBotMarkdown = (responseEvents: any[]): string => {
        const contentHits = responseEvents.filter(e => e.eventType === "ContentHit");
        if (props.simple) {
            // get speech started
            const assistantSpeech = responseEvents.filter(e => e.eventType === "AssistantSpeechStarted");
            if (assistantSpeech?.length) {
                const lastAssistantSpeech = assistantSpeech[assistantSpeech.length - 1];
                if (lastAssistantSpeech?.speechText?.length) {
                    return stripSsml(lastAssistantSpeech?.speechText);
                }
            }
            if (contentHits?.length) {
                const lastContentHit = contentHits[contentHits.length - 1];
                if (lastContentHit?.response?.data?.content?.length) {
                    return stripSsml(lastContentHit?.response?.data?.content);
                }
            }
            return "Unknown";
        }
        let md = "";
        if (contentHits?.length) {
            md = `${md}#### Content Hits: ${contentHits.length}\n&nbsp;\n\n`;
        }
        const anyAssistantSpeech = responseEvents.find(e => e.eventType === "AssistantSpeechStarted");
        for (const event of responseEvents) {
            if (event.eventType == "AssistantSpeechEnded") {
                if (event.early) {
                    if (event.secondsSpoken) {
                        // round seconds spoken to one decimal
                        md = `${md}#### Assistant interrupted after ${event?.secondsSpoken?.toFixed(1) ?? "?"} seconds\n&nbsp;\n`;                
                    } else {
                        md = `${md}#### Assistant interrupted\n&nbsp;\n`;                        
                    }
                }
            } else if (event.eventType == "AssistantSpeechStarted") {
                // compare event date of events
                // count number of assistant speech paused after this speech started
                const assistantSpeechPaused = responseEvents.filter(e =>
                    e.eventType === "AssistantSpeechPaused"
                    && e.timestamp > event.timestamp
                    && !responseEvents.some(se =>
                        se.eventType === "AssistantSpeechStarted"
                        && se.timestamp > event.timestamp
                        && se.timestamp < e.timestamp)
                );                
                if (assistantSpeechPaused?.length) {
                    md = `${md}#### Assistant Speech (Paused ${assistantSpeechPaused.length} times)\n`                    
                } else {
                    md = `${md}#### Assistant Speech\n`;
                }
                md = `${md}${stripSsml(event?.speechText ?? "unknown")}\n\n&nbsp;\n`;
            } else if (event.eventType == "ContentHit") {
                if (!anyAssistantSpeech) {
                    md = `${md}${stripSsml(event?.response?.data?.content ?? "unknown")}\n\n&nbsp;\n\n`;
                }
            } else if (event.eventType == "OrderPlaced") {
                md = `${md}#### Order placed with ${event?.totalItems ?? 0} \n&nbsp;\n\n`;
            } else if (event.eventType == "Frustration") {
                md = `${md}#### Frustration detected \n&nbsp;\n\n`;
            } else if (event.eventType == "ItemAddedToOrder") {
                md = `${md}#### Item(s) added to order \n&nbsp;\n\n`;
            } else if (event.eventType == "CallTransferred") {
                md = `${md}#### Call transferred \n&nbsp;\n\n`;
            } else if (event.eventType == "ImmediateTransfer") {
                md = `${md}#### Immediate Transfer \n&nbsp;\n\n`;
            }
        }
        // more exact trim
        while (md.endsWith("\n") || md.endsWith("&nbsp;")) {
            // if md ends with \n, remove it
            if (md.endsWith("\n")) {
                md = md.slice(0, -1);
            }
            // if md ends with &nbsp;, remove it
            if (md.endsWith("&nbsp;")) {
                md = md.slice(0, -6);
            }            
        }

        return md;
    }
    const generateCallCompleteMarkdown = (event): string => {
        let md = `Call Complete\n\nDuration: ${event.duration} seconds\n\n`;
        return md
    }
    const generateCallTransferred = (event): string => {
        let md = "Call Transferred";
        return md
    }
    const generateCallImmediateTransferMarkdown = (event): string => {
        let md = "Call Immediately Transferred";
        return md
    }
    const stripSsml = (ssml: string) => {
        return ssml.replace(/<[^>]*>/g, '');
    }
    // group by request id
    var requestIdEvents = props.events.filter(e => e.requestId?.length);
    const groupedEvents = requestIdEvents.reduce((acc, event) => {
        if (event.requestId in acc) {
            acc[event.requestId].push(event);
        } else {
            acc[event.requestId] = [event];
        }
        return acc;     
    }, {} as { [requestId: string]: any[] });
    const callComplete = props.events.find(e => e.eventType === "CallComplete");
    const callTransfer = props.events.find(e => e.eventType === "CallTransferred");
    const immediateTransfer = props.events.find(e => e.eventType === "ImmediateTransfer");
    return (
        <RelativeWrapper key={props?.events[0]?.sessionId} style={{
                height: props.manualHeight ? `${props.manualHeight}px` : "500px"
            }}
            className={`${props.inModal ? ' in-modal' : ""}`}>
            <HistoryBody  className={`chats-history-scrollable-body${props.hideBorder ? ' hide-border' : ""}${props.inModal ? ' in-modal' : ""}`}>
                <AllConversationsContainer className={`chats-conversations-container scrollable`}>
                    {!props.simple &&
                        <StyledButton
                            disabled={false}
                            themes={['secondary-small']}
                            text="All Events JSON"
                            onClick={() => props.setEventsToShow(props.events)}
                            type="button" />}
                    {Object.keys(groupedEvents).map((requestId, idx) => {
                        return renderRequestEventBubbles(requestId, groupedEvents[requestId], idx)
                    })}
                    {
                        callComplete &&
                        <LiveChatBubble markdown={generateCallCompleteMarkdown(callComplete)} sender={"bot"} />
                    }
                </AllConversationsContainer>
                {props.inModal ? <AlwaysScrollToBottom /> : <></>}
            </HistoryBody>
        </RelativeWrapper>
    ) 
}
const StyledButton = styled(Button)`
    width: 148px;
    margin-left: auto;
`;

const RequestDetails = styled.div`
`;


const RelativeWrapper = styled.div`
    background-color: ${color_shades_lighter};
    &.in-modal {
        padding-top: 16px;
    }
`
const HistoryBody = styled(CustomScrollbars)`
    color: ${color_shades_darkest};
    background-color: ${color_shades_lighter};
    margin-top: 16px;
    margin-bottom: 16px;
    &.in-modal {
        margin-top: 0px;
        margin-bottom: 0px;    
    }
`;

const AllConversationsContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0px 16px 16px 16px;
    overflow-wrap: break-word;
    word-break:break-word;
    &.scrollable {
        padding: 0px 16px 16px 16px;
    }
`;

const HiddenDiv = styled.div`
    display: none;
`;
export default SessionReview;